import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { GlassCard, Chip, Skeleton } from "@/v2/components/ui";
import { AV_FALLBACK } from "@/components/Widgets";
import { Crown, Medal, Award, Flame, CheckCircle2, ChevronUp, ChevronDown, Minus } from "lucide-react";

const filters = [
  { key: "overall", label: "عام" },
  { key: "week", label: "هذا الأسبوع" },
  { key: "month", label: "هذا الشهر" },
  { key: "department", label: "حسب الإدارة" },
];

const medalColor = (r) => (r === 1 ? "#e3c799" : r === 2 ? "#C7CCD1" : "#CD8B5B");
const trend = (id) => { const h = id.charCodeAt(0) % 3; return h === 0 ? "up" : h === 1 ? "down" : "same"; };

export default function Leaderboard() {
  const [filter, setFilter] = useState("overall");
  const { data: rows = [], isLoading } = useQuery({ queryKey: ["leaderboard", filter], queryFn: async () => (await api.get(`/leaderboard?filter=${filter}`)).data });

  const me = rows.find((r) => r.is_current);
  const ahead = me && me.rank > 1 ? rows[me.rank - 2] : null;
  const gap = ahead ? ahead.points - me.points : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold flex items-center gap-2"><Crown className="text-[#e3c799]" /> لوحة المتصدرين</h1>
        <p className="text-[#bcc8cf] mt-1">تنافس مع زملائك واصعد إلى القمة.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <button key={f.key} data-testid={`v2-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`px-5 h-10 rounded-full text-sm font-bold transition-all ${filter === f.key ? "v2-btn !h-10 !px-5" : "v2-btn-ghost !h-10 !px-5"}`}>{f.label}</button>
        ))}
      </div>

      {isLoading ? <Skeleton className="h-72 w-full" /> : (
        <>
          {/* podium */}
          <div className="grid grid-cols-3 gap-3 sm:gap-6 items-end">
            {[1, 0, 2].map((slot, pos) => {
              const r = rows[slot]; if (!r) return <div key={slot} />;
              const isFirst = r.rank === 1;
              return (
                <motion.div key={r.id} initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: pos * 0.1 }}>
                  <GlassCard gold={isFirst} className={`p-4 text-center relative ${isFirst ? "v2-glow-gold" : ""}`}>
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: medalColor(r.rank) }}>
                        {r.rank === 1 ? <Crown size={16} className="text-[#25343f]" /> : r.rank === 2 ? <Medal size={16} className="text-[#25343f]" /> : <Award size={16} className="text-[#25343f]" />}
                      </div>
                    </div>
                    <img src={r.avatar} alt={r.name} onError={AV_FALLBACK} className={`mx-auto rounded-full object-cover border-4 ${isFirst ? "w-20 h-20" : "w-14 h-14"}`} style={{ borderColor: medalColor(r.rank) }} />
                    <div className="font-bold mt-2 truncate text-sm">{r.name}</div>
                    <div className="text-[11px] text-[#bcc8cf] truncate">{r.department}</div>
                    <div className="text-xl font-extrabold v2-gold-text mt-1">{r.points}</div>
                    <div className={`mt-2 rounded-xl text-[11px] py-1 ${isFirst ? "bg-[#bb9661]/20 text-[#e3c799]" : "bg-white/5 text-[#bcc8cf]"}`}>المركز {r.rank}</div>
                  </GlassCard>
                </motion.div>
              );
            })}
          </div>

          {/* my gap */}
          {ahead && (
            <GlassCard gold className="p-4 flex items-center justify-between flex-wrap gap-2">
              <span className="text-sm text-[#bcc8cf]">أنت في المركز <span className="v2-gold-text font-bold">#{me.rank}</span></span>
              <span className="text-sm">تحتاج <span className="v2-emerald-text font-extrabold">{gap} نقطة</span> لتتجاوز <span className="font-bold">{ahead.name}</span></span>
            </GlassCard>
          )}

          {/* table */}
          <GlassCard className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead className="text-[#bcc8cf] border-b border-white/10">
                <tr>
                  <th className="p-4 text-start font-semibold">#</th>
                  <th className="p-4 text-start font-semibold">الموظف</th>
                  <th className="p-4 text-start font-semibold hidden sm:table-cell">الإدارة</th>
                  <th className="p-4 text-center font-semibold hidden sm:table-cell">التحديات</th>
                  <th className="p-4 text-center font-semibold hidden md:table-cell">متتالية</th>
                  <th className="p-4 text-center font-semibold">النقاط</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const t = trend(r.id);
                  return (
                    <tr key={r.id} data-testid={`v2-row-${r.rank}`} className={`border-b border-white/5 ${r.is_current ? "bg-[#bb9661]/10" : "hover:bg-white/5"}`}>
                      <td className="p-4 font-bold flex items-center gap-1">
                        {r.rank}
                        {t === "up" && <ChevronUp size={14} className="text-[#91cab8]" />}
                        {t === "down" && <ChevronDown size={14} className="text-[#F7A08A]" />}
                        {t === "same" && <Minus size={14} className="text-[#bcc8cf]" />}
                      </td>
                      <td className="p-4"><div className="flex items-center gap-3"><img src={r.avatar} alt={r.name} onError={AV_FALLBACK} className="w-9 h-9 rounded-full object-cover" /><span className="font-semibold">{r.name}{r.is_current && <span className="text-[#e3c799] text-xs"> (أنت)</span>}</span></div></td>
                      <td className="p-4 text-[#bcc8cf] hidden sm:table-cell">{r.department}</td>
                      <td className="p-4 text-center hidden sm:table-cell"><span className="inline-flex items-center gap-1"><CheckCircle2 size={14} className="text-[#91cab8]" /> {r.completed}</span></td>
                      <td className="p-4 text-center hidden md:table-cell"><span className="inline-flex items-center gap-1"><Flame size={14} className="text-[#F7A08A]" /> {r.streak}</span></td>
                      <td className="p-4 text-center font-extrabold v2-gold-text">{r.points}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </GlassCard>
        </>
      )}
    </div>
  );
}
